import { Mesh, SphereGeometry, MeshStandardMaterial, Vector3, Group } from 'three';
import {
  PLAYER_RADIUS,
  GRAVITY,
  THROTTLE_ACCEL,
  BRAKE_DECEL,
  TURN_RATE,
  AIR_CONTROL,
  MAX_SPEED,
  RESTITUTION,
  ROLL_RESIST,
  GROUND_SNAP,
  JUMP_VELOCITY,
  PHYSICS_HZ,
} from './config';
import { ChunkManager } from './terrain';

// Ball physics: full 3D velocity integrated under gravity, with a
// penetration-based ground contact. Contact only resolves when the ball is at
// or below the surface, so a fast ball naturally launches off crests (airtime),
// while gravity-along-slope + rolling friction make it roll downhill and settle
// in flat-bottomed depressions.
//
// Controls (camera-relative): W/Up = throttle, S/Down = brake/reverse,
// A/D = steer (rotate velocity), Space = jump.
export class Player {
  public mesh: Mesh;
  public group: Group;
  public position = new Vector3();
  public velocity = new Vector3();
  public grounded = false;
  public groundNormal = new Vector3(0, 1, 0);
  public speed = 0;
  public heading = 0; // movement yaw (rad), for the minimap

  private keys = new Set<string>();
  private jumpLatch = false;

  constructor() {
    const geo = new SphereGeometry(PLAYER_RADIUS, 32, 24);
    const mat = new MeshStandardMaterial({
      color: 0xff4444,
      roughness: 0.45,
      metalness: 0.25,
    });
    this.mesh = new Mesh(geo, mat);
    this.mesh.castShadow = true;

    this.group = new Group();
    this.group.add(this.mesh);
  }

  init(spawnX: number, spawnZ: number, groundY: number): void {
    this.position.set(spawnX, groundY + PLAYER_RADIUS, spawnZ);
    this.velocity.set(0, 0, 0);
    this.grounded = true;
    this.heading = Math.PI;
  }

  onKeyDown(key: string): void {
    this.keys.add(key);
    if (key === 'Space' && this.grounded && !this.jumpLatch) {
      this.velocity.y += JUMP_VELOCITY;
      this.grounded = false;
      this.jumpLatch = true;
    }
  }

  onKeyUp(key: string): void {
    this.keys.delete(key);
    if (key === 'Space') this.jumpLatch = false;
  }

  private readInput(): { throttle: number; brake: number; steer: number } {
    const throttle = this.keys.has('ArrowUp') || this.keys.has('KeyW') ? 1 : 0;
    const brake = this.keys.has('ArrowDown') || this.keys.has('KeyS') ? 1 : 0;
    const steer =
      (this.keys.has('ArrowRight') || this.keys.has('KeyD') ? 1 : 0) -
      (this.keys.has('ArrowLeft') || this.keys.has('KeyA') ? 1 : 0);
    return { throttle, brake, steer };
  }

  update(dtFrame: number, cm: ChunkManager, camYaw: number): void {
    if (dtFrame <= 0) return;
    const substeps = Math.max(1, Math.ceil(dtFrame * PHYSICS_HZ));
    const dt = dtFrame / substeps;
    for (let s = 0; s < substeps; s++) this.substep(dt, cm, camYaw);

    // NOTE: no world wrap — logical position is unbounded; the ChunkManager
    // rebases the render origin so GPU coords stay near zero, and sampleHeight
    // wraps internally. Wrapping here would desync from the per-tile rebase.
    this.speed = Math.hypot(this.velocity.x, this.velocity.z);
    if (this.speed > 0.05) {
      this.heading = Math.atan2(this.velocity.x, this.velocity.z);
    }
  }

  private substep(dt: number, cm: ChunkManager, camYaw: number): void {
    const pos = this.position;
    const vel = this.velocity;
    const r = PLAYER_RADIUS;
    const { throttle, brake, steer } = this.readInput();

    const hSp = Math.hypot(vel.x, vel.z);
    // Forward reference: where the ball is going, or the camera direction if slow.
    let fx: number, fz: number;
    if (hSp > 2) {
      fx = vel.x / hSp;
      fz = vel.z / hSp;
    } else {
      fx = Math.sin(camYaw);
      fz = Math.cos(camYaw);
    }

    if (this.grounded) {
      // Throttle along heading, projected onto the terrain tangent plane.
      // The engine only contributes up to MAX_SPEED; gravity/momentum may push
      // the ball beyond it (e.g. descending a long slope), so we gate the
      // throttle rather than clamping the total speed.
      if (throttle > 0) {
        const fwdSpeed = vel.x * fx + vel.z * fz;
        if (fwdSpeed < MAX_SPEED) {
          const n = this.groundNormal;
          const d = fx * n.x + fz * n.z;
          const tnx = fx - n.x * d;
          const tnz = fz - n.z * d;
          const l = Math.hypot(tnx, tnz);
          if (l > 1e-4) {
            vel.x += (tnx / l) * THROTTLE_ACCEL * dt;
            vel.z += (tnz / l) * THROTTLE_ACCEL * dt;
          }
        }
      }
      // Brake / reverse
      if (brake > 0) {
        const cur = Math.hypot(vel.x, vel.z);
        if (cur > 3) {
          const decel = BRAKE_DECEL * dt;
          const k = cur > decel ? (cur - decel) / cur : 0;
          vel.x *= k;
          vel.z *= k;
        } else {
          // Push backward from rest
          const n = this.groundNormal;
          const d = (-fx) * n.x + (-fz) * n.z;
          const tnx = -fx - n.x * d;
          const tnz = -fz - n.z * d;
          const l = Math.hypot(tnx, tnz);
          if (l > 1e-4) {
            vel.x += (tnx / l) * THROTTLE_ACCEL * 0.6 * dt;
            vel.z += (tnz / l) * THROTTLE_ACCEL * 0.6 * dt;
          }
        }
      }
      // Steering: rotate the horizontal velocity (responsive turning)
      if (steer !== 0 && hSp > 0.5) {
        const turn = steer * TURN_RATE * dt;
        const c = Math.cos(turn);
        const s = Math.sin(turn);
        const nvx = vel.x * c - vel.z * s;
        const nvz = vel.x * s + vel.z * c;
        vel.x = nvx;
        vel.z = nvz;
      }
    } else {
      // Air control (reduced). Gated by MAX_SPEED like the grounded throttle.
      if (throttle > 0) {
        const fwdSpeed = vel.x * fx + vel.z * fz;
        if (fwdSpeed < MAX_SPEED) {
          vel.x += fx * THROTTLE_ACCEL * AIR_CONTROL * dt;
          vel.z += fz * THROTTLE_ACCEL * AIR_CONTROL * dt;
        }
      }
      if (steer !== 0 && hSp > 0.5) {
        const turn = steer * TURN_RATE * AIR_CONTROL * dt;
        const c = Math.cos(turn);
        const s = Math.sin(turn);
        const nvx = vel.x * c - vel.z * s;
        const nvz = vel.x * s + vel.z * c;
        vel.x = nvx;
        vel.z = nvz;
      }
    }

    // --- Gravity (always full) ---
    vel.y -= GRAVITY * dt;

    // No hard speed clamp: throttle is gated by MAX_SPEED above, but gravity
    // and momentum are free to push the ball faster (e.g. on long descents).

    // --- Integrate ---
    pos.x += vel.x * dt;
    pos.y += vel.y * dt;
    pos.z += vel.z * dt;

    // --- Ground contact (only when at/below the surface) ---
    const gH = cm.sampleHeight(pos.x, pos.z);
    const n = cm.sampleNormal(pos.x, pos.z);
    const groundY = gH + r;

    if (pos.y <= groundY + GROUND_SNAP) {
      pos.y = groundY;
      this.grounded = true;
      this.groundNormal.copy(n);

      // Remove velocity into the surface (near-inelastic; small restitution)
      const vn = vel.x * n.x + vel.y * n.y + vel.z * n.z;
      if (vn < 0) {
        const remove = vn * (1 + RESTITUTION);
        vel.x -= n.x * remove;
        vel.y -= n.y * remove;
        vel.z -= n.z * remove;
      }

      // Coulomb-like rolling friction on the tangential velocity.
      const vtn = vel.x * n.x + vel.y * n.y + vel.z * n.z;
      const tvx = vel.x - n.x * vtn;
      const tvy = vel.y - n.y * vtn;
      const tvz = vel.z - n.z * vtn;
      const tvSp = Math.hypot(tvx, tvy, tvz);
      const decel = ROLL_RESIST * Math.max(0, n.y) * dt;
      if (tvSp <= decel) {
        vel.x -= tvx;
        vel.y -= tvy;
        vel.z -= tvz;
      } else {
        const f = decel / tvSp;
        vel.x -= tvx * f;
        vel.y -= tvy * f;
        vel.z -= tvz * f;
      }
    } else {
      this.grounded = false;
    }
  }
}
